import { createValidate } from 'typia'
import { parseQuotationLineDTO, QuotationLineDTO } from 'components/ordering/quotation/api/dtos/quotation-line-dto.js'
import { parseQuotationSaleDTO, QuotationSaleDTO } from 'components/ordering/quotation/api/dtos/quotation-sale-dto.js'
import {
  parseQuotationCostRuleDTO,
  QuotationCostRuleDTO
} from 'components/ordering/quotation/api/dtos/quotation-cost-rule-dto.js'
import { ParsingError, validateGuard } from 'components/shared/parsing/parsing.js'
import { QuotationStatus } from 'components/ordering/quotation/api/dtos/quotation-status.js'

export type IQuotationDTO = {
  id: number,
  party: number,
  user: number,
  email: string,
  status: QuotationStatus,
  name: string | null,
  external_reference: string | null,
  internal_reference: string | null,
  quotation_document_url: string | null,
  note: string | null,
  reason_of_decline: string | null,
  note_merchant: string | null,
  validity_date: string | null,
  create_date: string,
  quotation_lines: unknown[],
  is_closed: boolean,
  total_amount: string,
  sale: unknown,
  erp_cost_rule: unknown,
  can_approve: boolean,
  max_characters_name: number | null,
  name_mandatory?: boolean,
  can_add_to_basket: boolean,
  can_create_new_quotation: boolean,
  can_be_accepted: boolean,
  price_request_mode?: boolean,
  show_price?: boolean,
  show_quantity?: boolean,
}

export class QuotationDTO {
  public id: number
  public party: number
  public user: number
  public email: string
  public status: QuotationStatus
  public name: string | null
  public externalReference: string | null
  public internalReference: string | null
  public quotationDocumentUrl: string | null
  public note: string | null
  public reasonOfDecline: string | null
  public noteMerchant: string | null
  public validityDate: string | null
  public createDate: string
  public quotationLines: QuotationLineDTO[]
  public isClosed: boolean
  public totalAmount: string
  public sale: QuotationSaleDTO | null
  public erpCostRule: QuotationCostRuleDTO | null
  public canApprove: boolean
  public maxCharactersName: number | null
  public nameMandatory: boolean
  public canAddToBasket: boolean = true
  public canCreateNewQuotation: boolean = true
  public canBeAccepted: boolean = true
  public priceRequestMode: boolean
  public showPrice: boolean
  public showQuantity: boolean

  constructor(data: IQuotationDTO) {
    this.id = data.id
    this.party = data.party
    this.user = data.user
    this.email = data.email
    this.status = data.status
    this.name = data.name
    this.externalReference = data.external_reference
    this.internalReference = data.internal_reference
    this.quotationDocumentUrl = data.quotation_document_url
    this.note = data.note
    this.reasonOfDecline = data.reason_of_decline
    this.noteMerchant = data.note_merchant
    this.validityDate = data.validity_date
    this.createDate = data.create_date
    this.quotationLines = data.quotation_lines.map(parseQuotationLineDTO)
    this.isClosed = data.is_closed
    this.totalAmount = data.total_amount
    this.sale = parseQuotationSaleDTO(data.sale)
    this.erpCostRule = parseQuotationCostRuleDTO(data.erp_cost_rule)
    this.canApprove = data.can_approve
    this.maxCharactersName = data.max_characters_name
    this.nameMandatory = data.name_mandatory ?? false
    this.canAddToBasket = data.can_add_to_basket
    this.canCreateNewQuotation = data.can_create_new_quotation
    this.canBeAccepted = data.can_be_accepted
    this.priceRequestMode = data.price_request_mode ?? false
    this.showPrice = data.show_price ?? !this.priceRequestMode
    this.showQuantity = data.show_quantity ?? !this.priceRequestMode
  }
}

const validateSingle = createValidate<IQuotationDTO | null>()

export const parseQuotationDTO = (data: unknown) => {
  validateGuard(validateSingle, data, 'quotation')
  if (data === null) return null
  return new QuotationDTO(data)
}

const validateList = createValidate<IQuotationDTO[]>()

export const parseQuotationDTOList = (data: unknown) => {
  validateGuard(validateList, data, 'quotation list')
  return data.map(dto => new QuotationDTO(dto))
}

export const parseDefiniteQuotationDTO = (data: unknown) => {
  const quotationDTO = parseQuotationDTO(data)
  if (quotationDTO instanceof QuotationDTO) return quotationDTO

  throw new ParsingError('Received unexpected null', 'quotation')
}
